import { basicAuth } from "../standardAuthPatterns";
import { defineConnector } from "../types";
import logo from "./logo.svg";

// The US1 site is the only one whose API host has no region segment
// (api.sumologic.com); every other site is api.<site>.sumologic.com.
const sumoLogicApiUrl = (site: string) => {
  const domain =
    !site || site === "us1" ? "api.sumologic.com" : `api.${site}.sumologic.com`;
  return `https://${domain}`;
};

export const sumoLogic = defineConnector({
  identifier: "sumoLogic",
  name: "Sumo Logic",
  tagLine: "Log analytics and SIEM",
  keywords: "sumologic siem logs collectors search observability metrics",
  logo,
  protocol: "http",
  tags: ["observability", "security", "data"],
  defaultUrlPattern: () => "https://api\\..*sumologic\\.com/.*",
  auth: (app) =>
    basicAuth({
      username: app.config.accessId,
      password: app.secret.accessKey,
    }),
  steps: [
    {
      title: "Select your Sumo Logic site",
      items: [
        {
          kind: "select",
          label: "Site",
          required: true,
          target: { kind: "configuration", field: "site" },
          options: [
            { value: "us1", label: "US1 (api.sumologic.com)" },
            { value: "us2", label: "US2 (api.us2.sumologic.com)" },
            { value: "au", label: "AU (api.au.sumologic.com)" },
            { value: "ca", label: "CA (api.ca.sumologic.com)" },
            { value: "de", label: "DE (api.de.sumologic.com)" },
            { value: "eu", label: "EU (api.eu.sumologic.com)" },
            { value: "fed", label: "FED (api.fed.sumologic.com)" },
            { value: "jp", label: "JP (api.jp.sumologic.com)" },
            { value: "kr", label: "KR (api.kr.sumologic.com)" },
          ],
          defaultValue: "us1",
          helperText:
            "Select the site your Sumo Logic account is hosted on. You can identify this from your Sumo Logic URL.",
        },
      ],
    },
    {
      title: "Get your access credentials",
      items: [
        {
          kind: "markdown",
          content:
            "In your Sumo Logic console, navigate to **Administration > Security > Access Keys**. Click **Add Access Key**, enter a name, and click **Save**. Copy the Access ID and Access Key.",
        },
      ],
    },
    {
      title: "Enter your access credentials",
      items: [
        {
          kind: "input",
          label: "Access ID",
          required: true,
          target: { kind: "configuration", field: "accessId" },
        },
        {
          kind: "input",
          label: "Access Key",
          required: true,
          target: { kind: "secrets", field: "accessKey" },
        },
      ],
    },
  ],
  connectionTest: {
    protocol: "http",
    request: ({ site }) => ({
      url: `${sumoLogicApiUrl(site)}/v1/collectors`,
      expectedStatus: [200, 401, 403],
    }),
  },
  usageHint: () =>
    "Use process.env.SUMO_LOGIC_API_URL as the base URL for the Sumo Logic REST API.",
  publicEnvVars: ({ site }) => ({
    SUMO_LOGIC_SITE: site || "",
    SUMO_LOGIC_API_URL: sumoLogicApiUrl(site),
  }),
});
